import { useState, type FormEvent } from 'react';

const FORM_ID = 'kangua-newsletter';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahbo1bp14a1h3mr6hv0';

type Status = 'idle' | 'loading' | 'success' | 'error';

const NewsletterForm = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('phone_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    setStatus('loading');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') payload.append(key, value);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam || !response.ok
            ? serverMsg || '提交未通过校验，请检查后重试。'
            : serverMsg || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，未能提交，请稍后重试。');
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-mail-line text-base"></i>
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="你的邮箱地址"
            autoComplete="email"
            className="w-full rounded-full border border-background-300/70 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <select
          name="interest"
          defaultValue=""
          aria-label="感兴趣的分类"
          className="rounded-full border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="">选择感兴趣的方向</option>
          <option value="顶流追踪">顶流追踪</option>
          <option value="影视内幕">影视内幕</option>
          <option value="综艺现场">综艺现场</option>
          <option value="音乐动态">音乐动态</option>
          <option value="时尚品牌">时尚品牌</option>
          <option value="幕后资本">幕后资本</option>
        </select>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'loading' ? '提交中…' : '免费订阅'}
        </button>
      </div>

      <input
        type="text"
        name="phone_alt"
        className="field-aux-offset"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-3 min-h-[20px] text-sm" aria-live="polite">
        {status === 'success' && (
          <p className="flex items-center gap-1.5 text-accent-700">
            <i className="ri-checkbox-circle-line text-base"></i>
            订阅成功，最新一期爆料将在每周三发送到你的邮箱。
          </p>
        )}
        {status === 'error' && (
          <p className="flex items-center gap-1.5 text-primary-600">
            <i className="ri-error-warning-line text-base"></i>
            {formError || '提交失败，请稍后重试。'}
          </p>
        )}
      </div>
    </form>
  );
};

export default NewsletterForm;